import React, { useCallback, useEffect, useState } from 'react';
import Modal from '~/components/Modal';
import { MdClose } from 'react-icons/md';
import FormFooter from '~/components/form/form-footer';
import { useForm } from '~/hooks';
import { usePreferences } from '~/hooks/useAuth';
import { FormGroup, FormSelect } from '~/components/form';
import LenderLookupGrid from './LenderLookupGrid';
import { lender } from '~/api';
import { SectionHeader } from '../../../PolicySearchRequest/common';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';


/**
 * @typedef LenderLookupValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 'Your File Number'
 * @property {boolean}  addLenderFlag
 * @property {[object]} data
 * @property {boolean}  editLender 
 * 
 * @typedef FormState
 * @property {LenderLookupValues} values The current form values
 */

/** @type {LenderLookupValues} */
const defaultState = {
    id: 0,
    sectionId: 0,
    titleOpinionId: 0,
    lenderPrimary: null,
    stateOptions: [],
    data: [],
    addCustomLenderFlag: false,
    editLender: false
};


const LenderLookup = (props) => {
    const [lenderDetailData, setLenderDetailData] = useState(null);
    const [isCustomLender, setIsCustomLender] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);


    const loadData = async (values, props) => {
        const lenderList = (await lender.getLenders()).map(f => ({
            id: f.Id,
            lenderName: f.Name,
            lenderStreetAddress: f.Address1,
            lenderCity: f.City,
            lenderState: f.State,
            lenderZip: f.Zip
        })).sort((a, b) => a.lenderName.localeCompare(b.lenderName));


        return {
            ...values,
            ...props,
            lenderList: lenderList,
            lenderDetailData: props.lenderDetailData,
            sectionId: values ? values.sectionId : 0,
        };
    };

    const onLoad = useCallback((values, props) => {
        return loadData(values, props);
    }, [props.isModalOpen]);

    const { state, fieldActions } = useForm({
        initialState: { ...defaultState },
        props,
        onLoad,
        debug: true
    });

    const addCustomLender = () => {
        setErrorMessage(false);
        setIsCustomLender(true);
        return fieldActions.onChange(true, 'addCustomLenderFlag');
    };

    const onBlur = (value, path) => {
        setErrorMessage(false);
        return fieldActions.onChange(value, `lenderDetailData[${path}]`);
    };

    const { preferences, updatePreferences, preferencesChanged, reloadUser } = usePreferences();

    const addFavourite = async id => {
        if (preferences.lenders?.includes(id)) return;

        const lenders = preferences.lenders ? [...preferences.lenders, id] : [id];
        await updatePreferences({ ...preferences, lenders });
    };

    const removeFavourite = async id => {
        if (!preferences.lenders) return;

        const lenders = preferences.lenders.filter(lender => lender !== id);
        await updatePreferences({ ...preferences, lenders });
    };

    useEffect(() => {
        return () => {
            if (preferencesChanged) reloadUser();
        };
    }, [preferencesChanged, reloadUser]);

    return (
        <Modal isModalOpen={props.isModalOpen} preventScroll className='w-3/4 h-5/6 bg-clip-border bg-white rounded-md'>
            <section className='h-full flex flex-col'>
                <header className='pt-3 pb-2 px-8 border-b flex items-center justify-between'>
                    <p className='font-semibold'>{props.headerTxt}</p>
                    <button
                        className='rounded-full p-2 hover:bg-gray-150 focus:outline-none'
                        onClick={props.onCancel}
                    >
                        <MdClose />
                    </button>
                </header>
                <div className='flex-grow overflow-y-scroll'>
                    <div className="px-8 py-4 grid grid-cols-4 grid-flow-row gap-x-2 gap-y-2 text-sm mb-2 ">
                        {!state.values.lenderList ?

                            <div id="loading-modal" className="col-span-12 flex items-center justify-center h-1/3 w-2/3 z-50 absolute opacity-50">
                                <div>
                                    <AiOutlineLoading3Quarters className="animate-spin text-blue-30 font-bold" size="4rem" />
                                </div>
                            </div>
                            :
                            <LenderLookupGrid
                                path="lenderDetailData"
                                className="p-4 text-sm border-b"
                                data={state.values.lenderList ? state.values.lenderList : []}
                                onLenderSelect={(values) => {
                                    setErrorMessage(false);
                                    setIsCustomLender(false);
                                    setLenderDetailData(values);
                                    return fieldActions.onChange(values, 'lenderDetailData');
                                }}
                                favourites={preferences.lenders}
                                addFavourite={addFavourite}
                                removeFavourite={removeFavourite}
                            />
                        }


                        {isCustomLender ?
                            <div className='col-span-8'>
                                <SectionHeader colSpan={6}>Custom Lender</SectionHeader>
                                <div className="col-span-8 grid grid-flow-row grid-cols-12 gap-x-2 justify-start items-center">
                                    <FormGroup
                                        label="Lender Name"
                                        groupClassName="col-span-2 mt-4 mb-4"
                                        path='lenderName'
                                        value={state.values.lenderName}
                                        error={state.errors.lenderName}
                                        {...fieldActions}
                                        onBlur={onBlur}
                                    />
                                    <FormGroup
                                        label="Street Address"
                                        groupClassName="col-span-3 mt-4 mb-4"
                                        path='lenderStreetAddress'
                                        value={state.values.lenderStreetAddress}
                                        error={state.errors.lenderStreetAddress}
                                        {...fieldActions}
                                        onBlur={onBlur}
                                    />
                                    <FormGroup
                                        label="City"
                                        groupClassName="col-span-2 mt-4 mb-4"
                                        path='lenderCity'
                                        value={state.values.lenderCity}
                                        error={state.errors.lenderCity}
                                        {...fieldActions}
                                        onBlur={onBlur}
                                    />
                                    <FormSelect
                                        label="State"
                                        required
                                        options={props.stateOptions}
                                        type='select'
                                        isClearable={false}
                                        isSearchable
                                        groupClassName="col-span-3"
                                        path='lenderState'
                                        value={state.values.lenderState}
                                        error={state.errors.lenderState}
                                        {...fieldActions}
                                        onChange={(value) => {
                                            fieldActions.onChange(value.value, 'lenderDetailData[lenderState]');
                                            fieldActions.onChange(value, 'lenderState');
                                        }}
                                    />
                                    {/*
                                    <FormGroup
                                        label="State"
                                        groupClassName="col-span-1 mt-4 mb-4"
                                        path='lenderState'                 
                                        value={state.values.lenderState}
                                        error={state.errors.lenderState}
                                        {...fieldActions}
                                        onBlur={onBlur}
                                    />
                                    */}
                                    <FormGroup
                                        label="Zip Code"
                                        groupClassName="col-span-2 mt-4 mb-4"
                                        path='lenderZip'
                                        value={state.values.lenderZip}
                                        error={state.errors.lenderZip}
                                        {...fieldActions}
                                        onBlur={onBlur}
                                    />
                                </div>
                            </div>
                            :
                            <div className='col-span-6'>
                                <div className="flex justify-start items-center">
                                    <button
                                        className='py-2 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                                        onClick={() => addCustomLender()}
                                    >
                                        Enter Custom
                                    </button>
                                </div>

                            </div>
                        }
                        <div className="col-span-6 flex justify-start items-center">
                            {errorMessage ?
                                <div className="text-sm text-red-600 rounded-md py-1 h-8 mt-4">
                                    <p>Incomplete Information. Please select a lender or input all valid information.</p>
                                </div>
                                :
                                <div className="text-sm rounded-md px-2 py-1 h-8 -mt-4"> </div>
                            }
                        </div>
                        <div className="col-span-4 flex justify-end items-center">
                            <FormFooter
                                cancelBtnTxt={'Cancel'}
                                submitBtnTxt={
                                    props.editLender ? 'Save' : 'Add'}
                                onSubmit={() => {
                                    (lenderDetailData == null && !isCustomLender) || (isCustomLender
                                        && (Object.keys(state.values.lenderDetailData).length == 0 || Object.values(state.values.lenderDetailData).filter(x => x == '').length > 0)) ?
                                        setErrorMessage(true)
                                        :
                                        (props.editLender ?
                                            props.saveLender(state.values)
                                            :
                                            props.addLender(isCustomLender ? state.values.lenderDetailData : lenderDetailData));
                                }}
                                onCancel={props.onCancel}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </Modal>
    );
};

export default LenderLookup;

